import BlogLayout from '@/pages/blog/components/BlogLayout';
import Breadcrumbs from '@/pages/blog/components/Breadcrumbs';
import AuthorBio from '@/pages/blog/components/AuthorBio';
import RelatedArticles from '@/pages/blog/components/RelatedArticles';
import TableOfContents from '@/pages/blog/components/TableOfContents';

const title = "Beginner's Guide to Strength Training for Men Over 60";
const description = "Start strength training after 60 the right way. Joint-safe exercises, beginner routines, and real results. Get Mason Hale's proven blueprint today.";

const schema = JSON.stringify([
  {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "The Complete Beginner's Guide to Strength Training for Men Over 60",
    "author": { "@type": "Person", "name": "Mason Hale", "url": "https://masonhalefit.com" },
    "publisher": { "@type": "Organization", "name": "Mason Hale Fitness", "url": "https://masonhalefit.com" },
    "datePublished": "2025-03-15",
    "dateModified": "2025-04-06",
    "url": "https://masonhalefit.com/blog/strength-training-for-men-over-60-beginners-guide",
    "description": description
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      { "@type": "Question", "name": "Is it safe to start strength training at 60?", "acceptedAnswer": { "@type": "Answer", "text": "Yes. Research from the NIH shows that strength training is not only safe for men over 60 but is one of the most effective ways to preserve muscle mass, bone density, and metabolic health. The key is starting with appropriate weights and joint-safe movement patterns." } },
      { "@type": "Question", "name": "How many days a week should a 60-year-old man lift weights?", "acceptedAnswer": { "@type": "Answer", "text": "2-3 days per week is ideal for beginners over 60. This allows adequate recovery time between sessions, which becomes more important as we age. Full-body workouts 3x per week or upper/lower splits 4x per week both work well." } },
      { "@type": "Question", "name": "What weights should a 60-year-old man start with?", "acceptedAnswer": { "@type": "Answer", "text": "Start lighter than you think you need to. A weight you can lift for 12-15 reps with good form is a good starting point. The goal in the first 4-6 weeks is learning movement patterns, not maxing out." } },
      { "@type": "Question", "name": "How long before a man over 60 sees results from strength training?", "acceptedAnswer": { "@type": "Answer", "text": "Most men notice strength improvements within 2-3 weeks (neurological adaptations) and visible muscle changes within 6-8 weeks of consistent training. Consistency matters far more than intensity at the start." } },
      { "@type": "Question", "name": "Do I need a gym to start strength training over 60?", "acceptedAnswer": { "@type": "Answer", "text": "No. A pair of adjustable dumbbells and your own bodyweight is enough to build real strength. Many men over 60 get excellent results training entirely at home." } }
    ]
  }
]);

const tocItems = [
  { id: 'why-it-matters', label: 'Why Strength Training Matters More After 60' },
  { id: 'what-happens', label: "What Happens to Your Body If You Don't Lift" },
  { id: 'getting-started', label: 'How to Get Started: The Right Way' },
  { id: 'best-exercises', label: 'The Best Beginner Exercises for Men Over 60' },
  { id: 'sample-routine', label: 'A Simple 3-Day Beginner Routine' },
  { id: 'safety', label: 'Safety First: Protecting Your Joints' },
  { id: 'common-mistakes', label: 'Common Mistakes Beginners Make' },
  { id: 'faq', label: 'Frequently Asked Questions' },
];

const related = [
  { slug: 'best-exercises-for-men-over-60', title: '15 Best Exercises for Men Over 60 (Joint-Safe & Effective)', category: 'Strength Training' },
  { slug: 'how-to-build-muscle-after-60', title: 'How to Build Muscle After 60: The Science-Backed Approach', category: 'Strength Training' },
  { slug: 'fitness-after-60-where-to-start', title: 'Fitness After 60: Where the Hell Do You Even Start?', category: 'Motivation' },
];

export default function StrengthTrainingBeginnersGuidePage() {
  return (
    <BlogLayout
      title={title}
      description={description}
      keywords="strength training for men over 60, beginner strength training over 60, weight lifting after 60"
      canonicalPath="/blog/strength-training-for-men-over-60-beginners-guide"
      datePublished="2025-03-15"
      dateModified="2025-04-06"
      schemaMarkup={schema}
    >
      <Breadcrumbs title="Beginner's Guide to Strength Training for Men Over 60" />

      <div className="mb-3">
        <span className="text-[#c8a96e] text-xs font-bold tracking-[0.2em] uppercase">Strength Training</span>
      </div>

      <h1 className="text-white text-3xl sm:text-4xl font-black leading-tight mb-4">
        The Complete Beginner's Guide to Strength Training for Men Over 60
      </h1>

      <div className="flex items-center gap-4 mb-8 pb-8 border-b border-white/10">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 flex items-center justify-center bg-[#c8a96e] rounded-full flex-shrink-0">
            <i className="ri-user-fill text-black text-xs" aria-hidden="true" />
          </div>
          <span className="text-gray-400 text-xs">Mason Hale</span>
        </div>
        <span className="text-gray-600 text-xs">March 15, 2025</span>
        <span className="text-gray-600 text-xs">12 min read</span>
      </div>

      <TableOfContents items={tocItems} />

      <div className="prose-blog">
        <p className="text-gray-300 text-base leading-relaxed mb-5">
          Let me be straight with you: I was 62 years old, 55 pounds overweight, and on three different medications when I finally decided to do something about it. I'd tried diets. I'd tried cardio. Nothing stuck.
        </p>
        <p className="text-gray-300 text-base leading-relaxed mb-8">
          What actually changed everything was <strong className="text-white">strength training for men over 60</strong>. Not the kind you see in bodybuilding magazines — the joint-safe, practical kind that fits real life. This guide is everything I wish I'd known when I started.
        </p>

        <h2 id="why-it-matters" className="text-white text-2xl font-black mt-10 mb-4">Why Strength Training Matters More After 60</h2>
        <p className="text-gray-300 text-base leading-relaxed mb-4">
          After 60, your body starts losing muscle at an accelerated rate — a process called sarcopenia. According to the{' '}
          <a href="https://www.nia.nih.gov/health/exercise-physical-activity/exercise-older-adults" target="_blank" rel="nofollow noopener noreferrer" className="text-[#c8a96e] hover:text-[#d9bc82] transition-colors">National Institute on Aging</a>,
          adults can lose 3-5% of muscle mass per decade after 30, and that rate accelerates significantly after 60.
        </p>
        <p className="text-gray-300 text-base leading-relaxed mb-4">
          Strength training is the only proven way to reverse this. Not slow it down — actually reverse it.
        </p>
        <p className="text-gray-300 text-base leading-relaxed mb-6">
          Here's what regular strength training does for men over 60:
        </p>
        <ul className="space-y-2 mb-8 pl-4">
          {['Rebuilds lost muscle mass', 'Increases bone density (reducing fracture risk)', 'Boosts metabolism — burns more calories at rest', 'Improves insulin sensitivity and blood sugar control', 'Reduces joint pain by strengthening surrounding muscles', 'Improves balance and reduces fall risk', 'Elevates testosterone naturally', 'Improves sleep quality'].map((item) => (
            <li key={item} className="flex items-start gap-3 text-gray-300 text-sm">
              <i className="ri-checkbox-circle-fill text-[#c8a96e] mt-0.5 flex-shrink-0" aria-hidden="true" />
              {item}
            </li>
          ))}
        </ul>

        <h2 id="what-happens" className="text-white text-2xl font-black mt-10 mb-4">What Happens to Your Body If You Don't Lift</h2>
        <p className="text-gray-300 text-base leading-relaxed mb-4">
          This isn't meant to scare you — it's meant to motivate you. Without resistance training, here's what happens after 60:
        </p>
        <ul className="space-y-2 mb-6 pl-4">
          {['Muscle loss accelerates — up to 1-2% per year', 'Metabolism slows, making weight gain easier', 'Bone density decreases, raising osteoporosis risk', 'Balance deteriorates, increasing fall risk', 'Blood sugar regulation worsens', 'Energy levels drop'].map((item) => (
            <li key={item} className="flex items-start gap-3 text-gray-300 text-sm">
              <i className="ri-close-circle-fill text-red-400 mt-0.5 flex-shrink-0" aria-hidden="true" />
              {item}
            </li>
          ))}
        </ul>
        <p className="text-gray-300 text-base leading-relaxed mb-8">
          The good news? Every single one of these is reversible with consistent <strong className="text-white">strength training for men over 60</strong>. I'm living proof.
        </p>

        <h2 id="getting-started" className="text-white text-2xl font-black mt-10 mb-4">How to Get Started: The Right Way</h2>
        <p className="text-gray-300 text-base leading-relaxed mb-4">
          Most guys over 60 make the same mistake: they try to train like they did at 30. That's a fast track to injury. Here's the smarter approach:
        </p>

        <h3 className="text-white text-lg font-bold mt-6 mb-3">Step 1: Get Medical Clearance</h3>
        <p className="text-gray-300 text-base leading-relaxed mb-4">
          If you have any existing conditions — heart disease, diabetes, joint issues — talk to your doctor first. Most will enthusiastically support strength training. The{' '}
          <a href="https://www.mayoclinic.org/healthy-lifestyle/fitness/in-depth/strength-training/art-20046670" target="_blank" rel="nofollow noopener noreferrer" className="text-[#c8a96e] hover:text-[#d9bc82] transition-colors">Mayo Clinic</a>{' '}
          recommends strength training for virtually all older adults.
        </p>

        <h3 className="text-white text-lg font-bold mt-6 mb-3">Step 2: Start With Bodyweight</h3>
        <p className="text-gray-300 text-base leading-relaxed mb-4">
          Before you touch a weight, master the basic movement patterns with your own bodyweight. Squats, hinges, pushes, pulls. This builds the foundation and teaches your joints the right mechanics.
        </p>

        <h3 className="text-white text-lg font-bold mt-6 mb-3">Step 3: Add Resistance Gradually</h3>
        <p className="text-gray-300 text-base leading-relaxed mb-6">
          Once bodyweight movements feel solid, add light resistance. Dumbbells, resistance bands, or machines all work. The key is progressive overload — gradually increasing the challenge over time.
        </p>

        <div className="bg-[#111111] border border-[#c8a96e]/20 rounded-lg p-6 mb-8">
          <p className="text-[#c8a96e] text-xs font-bold tracking-[0.2em] uppercase mb-2">Pro Tip</p>
          <p className="text-gray-300 text-sm leading-relaxed">
            The <a href="https://masonhalefit.com" className="text-[#c8a96e] hover:text-[#d9bc82] transition-colors font-semibold">Over-60 Strength Blueprint</a> lays out exactly this progression — from zero to a complete strength training routine — specifically designed for men over 60 with joint safety built in from day one.
          </p>
        </div>

        <h2 id="best-exercises" className="text-white text-2xl font-black mt-10 mb-4">The Best Beginner Exercises for Men Over 60</h2>
        <p className="text-gray-300 text-base leading-relaxed mb-6">
          These are the foundational movements every man over 60 should master. They hit all the major muscle groups while being gentle on the joints.
        </p>

        <div className="space-y-4 mb-8">
          {[
            { name: 'Goblet Squat', desc: 'Builds leg strength without spinal compression. Easier on knees than barbell squats.' },
            { name: 'Romanian Deadlift', desc: 'Strengthens the posterior chain — hamstrings, glutes, lower back — with minimal joint stress.' },
            { name: 'Seated Dumbbell Press', desc: 'Builds shoulder and chest strength with back support.' },
            { name: 'Dumbbell Row', desc: 'Builds back strength and improves posture. Critical for men who sit a lot.' },
            { name: 'Wall Push-Up / Incline Push-Up', desc: 'Builds chest and tricep strength with adjustable difficulty.' },
            { name: 'Resistance Band Pull-Apart', desc: 'Strengthens the rear deltoids and improves shoulder health.' },
          ].map((ex) => (
            <div key={ex.name} className="flex items-start gap-4 bg-[#111111] border border-white/8 rounded-lg p-4">
              <div className="w-8 h-8 flex items-center justify-center bg-[#c8a96e]/10 rounded-sm flex-shrink-0">
                <i className="ri-heart-pulse-line text-[#c8a96e] text-sm" aria-hidden="true" />
              </div>
              <div>
                <p className="text-white text-sm font-bold mb-1">{ex.name}</p>
                <p className="text-gray-400 text-sm">{ex.desc}</p>
              </div>
            </div>
          ))}
        </div>

        <h2 id="sample-routine" className="text-white text-2xl font-black mt-10 mb-4">A Simple 3-Day Beginner Routine</h2>
        <p className="text-gray-300 text-base leading-relaxed mb-6">
          Here's a simple full-body routine to get you started. Do this 3 days per week with at least one rest day between sessions.
        </p>

        <div className="overflow-x-auto mb-8">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left text-[#c8a96e] font-bold py-3 pr-4">Exercise</th>
                <th className="text-left text-[#c8a96e] font-bold py-3 pr-4">Sets</th>
                <th className="text-left text-[#c8a96e] font-bold py-3 pr-4">Reps</th>
                <th className="text-left text-[#c8a96e] font-bold py-3">Rest</th>
              </tr>
            </thead>
            <tbody>
              {[
                ['Goblet Squat', '2-3', '10-12', '90 sec'],
                ['Romanian Deadlift', '2-3', '10-12', '90 sec'],
                ['Dumbbell Row', '2-3', '10-12 each', '90 sec'],
                ['Seated DB Press', '2-3', '10-12', '90 sec'],
                ['Wall Push-Up', '2', '10-15', '60 sec'],
                ['Band Pull-Apart', '2', '15', '60 sec'],
              ].map(([ex, sets, reps, rest]) => (
                <tr key={ex} className="border-b border-white/5">
                  <td className="text-gray-300 py-3 pr-4">{ex}</td>
                  <td className="text-gray-400 py-3 pr-4">{sets}</td>
                  <td className="text-gray-400 py-3 pr-4">{reps}</td>
                  <td className="text-gray-400 py-3">{rest}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <h2 id="safety" className="text-white text-2xl font-black mt-10 mb-4">Safety First: Protecting Your Joints</h2>
        <p className="text-gray-300 text-base leading-relaxed mb-4">
          Joint safety is non-negotiable when you're doing <strong className="text-white">strength training for men over 60</strong>. Here are the rules I follow:
        </p>
        <ul className="space-y-2 mb-8 pl-4">
          {[
            'Always warm up for 5-10 minutes before lifting',
            'Never sacrifice form for heavier weight',
            'Stop if you feel sharp or joint pain (muscle burn is fine)',
            'Progress slowly — add weight in small increments',
            'Prioritize sleep and recovery — this is when you actually get stronger',
            'Stay hydrated — joints need it',
          ].map((item) => (
            <li key={item} className="flex items-start gap-3 text-gray-300 text-sm">
              <i className="ri-shield-check-fill text-[#c8a96e] mt-0.5 flex-shrink-0" aria-hidden="true" />
              {item}
            </li>
          ))}
        </ul>

        <h2 id="common-mistakes" className="text-white text-2xl font-black mt-10 mb-4">Common Mistakes Beginners Make</h2>
        <ul className="space-y-3 mb-8">
          {[
            { mistake: 'Going too heavy too fast', fix: 'Start lighter than you think you need to. Build the habit first.' },
            { mistake: 'Skipping the warm-up', fix: '5-10 minutes of light movement before every session. Non-negotiable.' },
            { mistake: 'Training through pain', fix: 'Muscle soreness is fine. Joint pain is a stop sign.' },
            { mistake: 'Not eating enough protein', fix: 'Aim for 0.7-1g of protein per pound of bodyweight daily.' },
            { mistake: 'Expecting overnight results', fix: 'Give it 8 weeks of consistency before judging. The results will come.' },
          ].map(({ mistake, fix }) => (
            <li key={mistake} className="bg-[#111111] border border-white/8 rounded-lg p-4">
              <p className="text-red-400 text-sm font-bold mb-1">Mistake: {mistake}</p>
              <p className="text-gray-300 text-sm">Fix: {fix}</p>
            </li>
          ))}
        </ul>

        <div className="bg-[#111111] border border-[#c8a96e]/30 rounded-lg p-7 mb-10 text-center">
          <p className="text-[#c8a96e] text-xs font-bold tracking-[0.2em] uppercase mb-3">Ready to Start?</p>
          <p className="text-white text-lg font-black mb-3">Get The Complete System — Not Just Tips</p>
          <p className="text-gray-400 text-sm mb-5 max-w-md mx-auto">
            The <a href="https://masonhalefit.com" className="text-[#c8a96e] hover:text-[#d9bc82] transition-colors font-semibold">Over-60 Strength Blueprint</a> gives you the complete joint-safe training system — workouts, progressions, and the exact approach I used to lose 55 lbs and get off 3 medications.
          </p>
          <a
            href="https://masonhalefit.com"
            className="inline-flex items-center gap-2 px-8 py-3 bg-[#c8a96e] text-black text-sm font-black tracking-[0.15em] uppercase rounded-sm hover:bg-[#d9bc82] transition-all cursor-pointer whitespace-nowrap"
          >
            Get The Blueprint — $19.99
          </a>
        </div>

        <h2 id="faq" className="text-white text-2xl font-black mt-10 mb-6">Frequently Asked Questions</h2>
        <div className="space-y-4 mb-10">
          {[
            { q: 'Is it safe to start strength training at 60?', a: 'Yes. Research from the NIH shows that strength training is not only safe for men over 60 but is one of the most effective ways to preserve muscle mass, bone density, and metabolic health. The key is starting with appropriate weights and joint-safe movement patterns.' },
            { q: 'How many days a week should a 60-year-old man lift weights?', a: '2-3 days per week is ideal for beginners over 60. This allows adequate recovery time between sessions, which becomes more important as we age.' },
            { q: 'What weights should a 60-year-old man start with?', a: 'Start lighter than you think you need to. A weight you can lift for 12-15 reps with good form is a good starting point. The goal in the first 4-6 weeks is learning movement patterns, not maxing out.' },
            { q: 'How long before a man over 60 sees results from strength training?', a: 'Most men notice strength improvements within 2-3 weeks and visible muscle changes within 6-8 weeks of consistent training.' },
            { q: 'Do I need a gym to start strength training over 60?', a: 'No. A pair of adjustable dumbbells and your own bodyweight is enough to build real strength. Many men over 60 get excellent results training entirely at home.' },
          ].map(({ q, a }) => (
            <div key={q} className="border border-white/10 rounded-lg p-5">
              <p className="text-white text-sm font-bold mb-2">{q}</p>
              <p className="text-gray-400 text-sm leading-relaxed">{a}</p>
            </div>
          ))}
        </div>
      </div>

      <AuthorBio />
      <RelatedArticles articles={related} />
    </BlogLayout>
  );
}
